import React, { useEffect, useState } from 'react';
import API from '../../services/api';
import ProgressRing from '../ui/ProgressRing';
import Icon from '../ui/Icon';
import RecommendationCard from '../recommendations/RecommendationCard';
import { scoreColor, scoreBand } from '../diagnostic/diagnosticHelpers';
import LevelUp from '../player/LevelUp';
import MotivationMoment from '../player/MotivationMoment';
import './TestResultScreen.css';

// Shared result experience for both the proctored flow (TakeTest.js) and
// practice flow (MockTestTake.js) — replaces the two independently
// hand-duplicated `.tt-result-*` JSX blocks that used to live inline in
// each page. Renders ONLY from the `result` object each page already
// computed (from the real submit-response payload) — no score/grading is
// recalculated here.
//
// Hierarchy, per the UX spec: Summary → Interpretation → Evidence → Next
// action. Gamification (personal-best / first-attempt / XP / level-up) is
// opt-in and entirely evidence-based — never shown if it can't be shown
// honestly (fetch failure, no prior data to compare against).
//
// `result` shapes actually returned by the two backends (see
// testController.submitTest vs mockTestController.submitMockTest):
//   official: { score, correct, wrong, total, percentage, unattempted?,
//               forceCheat?, timeUp?, cheating_flag?, already_submitted?, error? }
//   practice: { correct, wrong, total, skipped, percentage, topic_breakdown? }
//
// `playerDelta` (optional): { xpGained, fromLevel, toLevel } — a real
// before/after diff of GET /portal/player-profile the calling page took
// around the submit call. Never computed in here.
const TestResultScreen = ({
  result, variant, onPrimaryAction, onSecondaryAction, secondaryLabel, contextLabel,
  onReviewAction, playerDelta,
}) => {
  const [achievement, setAchievement] = useState(null); // { kind: 'best'|'first', label }
  const [nextMove, setNextMove] = useState(null);
  const [showLevelUp, setShowLevelUp] = useState(!!(playerDelta && playerDelta.toLevel > playerDelta.fromLevel));

  const pct = Number(result.percentage) || 0;
  const isOfficial = variant === 'official';
  const headlineScore = isOfficial ? (result.score ?? result.correct) : result.correct;
  const unanswered = isOfficial ? (result.unattempted ?? 0) : (result.skipped ?? 0);
  const flagged = isOfficial && (result.forceCheat || result.timeUp || result.cheating_flag);

  // Best-effort: the recommendation engine's top pick for what to do next (never invented here).
  useEffect(() => {
    if (result.error || result.already_submitted || result.alreadySubmitted) return;
    API.get('/portal/recommendations').then(res => setNextMove(res.data?.recommendations?.[0] || null)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Best-effort, never blocks the result from rendering. Only ever shows
    // a genuinely-earned achievement — silently shows nothing on any
    // ambiguity (fetch failure, too little history to compare against).
    //
    // Official-only: `overallStats.tests_taken` and `recentAttempts` both
    // come from GET /portal/dashboard, which is scoped to OFFICIAL
    // (diagnostic/aptitude) attempts only — mock/practice submissions never
    // touch either field (the same mock-isolation gate used throughout the
    // backend). Checking them after a PRACTICE submission would compare
    // this session against unrelated official-test history, or show a
    // "first assessment" badge driven entirely by stale, pre-existing
    // official-test data that has nothing to do with the session just
    // completed — so this comparison only runs for the official variant.
    if (!isOfficial || result.error || result.already_submitted || result.alreadySubmitted) return;
    API.get('/portal/dashboard').then(res => {
      const { overallStats, recentAttempts } = res.data || {};
      if (overallStats?.tests_taken === 1) {
        setAchievement({ kind: 'first', label: 'First assessment complete' });
        return;
      }
      const priorPercentages = (recentAttempts || [])
        .filter(a => a.total_marks > 0)
        .map(a => (a.score / a.total_marks) * 100);
      if (priorPercentages.length >= 2 && pct >= Math.max(...priorPercentages) && pct > 0) {
        setAchievement({ kind: 'best', label: 'New personal best' });
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const interpretation = () => {
    // Deliberately uncertain rather than asserting the attempt was saved —
    // this branch is only reached when the server responded with an error
    // (a pure network failure never reaches this screen at all, see
    // TakeTest.js/MockTestTake.js's doSubmit/handleSubmit), but a
    // server-side error can still mean the write didn't complete. Claiming
    // "it's been recorded" here would be a guess presented as fact.
    if (result.error) return "We couldn't confirm your result right now. Check back in a few minutes, or contact your administrator if this persists.";
    if (result.timeUp) return "Time ran out — here's what you completed before the timer ended.";
    if (result.forceCheat) return 'This session was auto-submitted after repeated proctoring violations.';
    if (pct >= 85) return "Excellent work — you're clearly strong on this material.";
    if (pct >= 70) return "Good session — you're solidly on track here.";
    if (pct >= 50) return "A fair result — there's a clear next step to build on this.";
    return "This one was tough, and that's useful information: it points straight at what to practice next.";
  };

  const motivationContext = () => {
    if (result.error) return null;
    if (achievement?.kind === 'best') return 'improvement';
    if (pct < 50) return 'post_difficult_result';
    return 'post_completion';
  };

  return (
    <div className="tr-screen">
      {showLevelUp && playerDelta && (
        <LevelUp fromLevel={playerDelta.fromLevel} toLevel={playerDelta.toLevel} onDismiss={() => setShowLevelUp(false)} />
      )}

      <div className={`tr-card ${achievement ? 'tr-card--achieved' : ''}`}>
        {!result.error && (
          <div className="tr-eyebrow"><Icon name="check" size={14} strokeWidth={3} /> {isOfficial ? 'Assessment complete' : 'Challenge complete'}</div>
        )}

        {achievement && (
          <div className={`tr-achievement tr-achievement--${achievement.kind}`}>
            <Icon name={achievement.kind === 'best' ? 'trophy' : 'bolt'} art={achievement.kind === 'best'} size={achievement.kind === 'best' ? 28 : 18} className="tr-achievement-icon" />
            {achievement.label}
          </div>
        )}

        {/* ── Summary ── */}
        {result.error ? (
          // No score ring, no correct/wrong/unanswered breakdown here — we
          // genuinely don't know the outcome (that's the whole point of
          // this branch), so a 0/0/0 stat row would look like real data
          // that just happens to be all zeros. Numbers-free by design.
          <div className="tr-summary-text">
            <h2 className="tr-title">Result unavailable</h2>
            {contextLabel && <p className="tr-context-label">{contextLabel}</p>}
          </div>
        ) : (
          <div className="tr-summary">
            <ProgressRing value={Math.round(pct)} color={scoreColor(pct)} size={110} strokeWidth={10} label="%" />
            <div className="tr-summary-text">
              <h2 className="tr-title">{flagged ? 'Session ended early' : scoreBand(pct)}</h2>
              <p className="tr-score-line">
                {isOfficial ? `${headlineScore} marks` : `${headlineScore} correct`} · {result.total} question{result.total === 1 ? '' : 's'}
              </p>
              {contextLabel && <p className="tr-context-label">{contextLabel}</p>}
              {playerDelta && playerDelta.xpGained > 0 && (
                <span className="tr-xp-chip"><Icon name="xp" size={14} /> +{playerDelta.xpGained} XP{playerDelta.toLevel > playerDelta.fromLevel ? ` · Level ${playerDelta.toLevel}` : ''}</span>
              )}
            </div>
          </div>
        )}

        {/* ── Interpretation ── */}
        <p className="tr-interpretation">{interpretation()}</p>

        {motivationContext() && <MotivationMoment context={motivationContext()} seed={`${result.correct}-${result.total}`} />}

        {flagged && !result.error && (
          <div className="tr-flag-note">
            {result.forceCheat && 'Repeated tab/fullscreen violations were detected during this session.'}
            {result.timeUp && !result.forceCheat && 'The timer reached zero before you finished every question.'}
            {result.cheating_flag && !result.forceCheat && !result.timeUp && 'This attempt was flagged for review.'}
          </div>
        )}

        {/* ── Evidence ── */}
        {!result.error && (
          <div className="tr-evidence">
            <div className="tr-ev"><span className="tr-ev-val" style={{ color: 'var(--z-success)' }}>{result.correct}</span><span className="tr-ev-label">Correct</span></div>
            <div className="tr-ev"><span className="tr-ev-val" style={{ color: 'var(--z-danger)' }}>{result.wrong}</span><span className="tr-ev-label">Wrong</span></div>
            <div className="tr-ev"><span className="tr-ev-val" style={{ color: 'var(--z-muted)' }}>{unanswered}</span><span className="tr-ev-label">Unanswered</span></div>
          </div>
        )}

        {!isOfficial && !result.error && result.topic_breakdown && Object.keys(result.topic_breakdown).length > 0 && (
          <div className="tr-topics">
            <div className="tr-topics-label">By topic</div>
            {Object.entries(result.topic_breakdown).map(([topic, s]) => {
              const tPct = s.total > 0 ? (s.correct / s.total) * 100 : 0;
              return (
                <div className="tr-topic-row" key={topic}>
                  <span className="tr-topic-name">{topic}</span>
                  <div className="tr-topic-track"><div className="tr-topic-fill" style={{ width: `${tPct}%`, background: scoreColor(tPct) }} /></div>
                  <span className="tr-topic-pct" style={{ color: scoreColor(tPct) }}>{Math.round(tPct)}%</span>
                </div>
              );
            })}
          </div>
        )}

        {nextMove && (
          <div className="tr-next">
            <div className="tr-topics-label">Your next move</div>
            <RecommendationCard rec={nextMove} variant="compact" />
          </div>
        )}

        {/* ── Next action ── */}
        <div className="tr-actions">
          <button type="button" className="tr-btn-primary" onClick={onPrimaryAction}><Icon name="progress" size={18} /> View progress</button>
          {onReviewAction && !result.error && (
            <button type="button" className="tr-btn-secondary" onClick={onReviewAction}><Icon name="list" size={18} /> Review answers</button>
          )}
          <button type="button" className="tr-btn-secondary" onClick={onSecondaryAction}>{secondaryLabel}</button>
        </div>
      </div>
    </div>
  );
};

export default TestResultScreen;
